// SpecsGrid — the 4-column row of stat tiles shown under the price on
// both detail surfaces. Always renders four columns, padding missing
// fields with em-dash placeholders so the grid stays aligned. The
// `surface` prop picks the tile background tint so it sits cleanly
// inside the card (`background`) or stays on the page surface
// (`card`, default).

import { Bath, BedDouble, Building2, Coins, Columns3, Sofa, Square } from "lucide-react";

import { cn } from "@/lib/utils";
import { formatBrokerageFee } from "@/features/property/utils/property-display";
import { propertyFurnitureLabel } from "@/features/property/utils/property-enum-options";
import type { Property } from "@/types/property";

interface SpecsGridProps {
  property: Property;
  surface?: "card" | "background";
}

export function SpecsGrid({
  property,
  surface = "card",
}: SpecsGridProps) {
  const cells: Array<{
    icon: React.ReactNode;
    value: number | string;
    unit?: string;
    label: string;
  }> = [
    {
      icon: <Square aria-hidden="true" className="size-3" />,
      value: property.area,
      unit: "m²",
      label: "Diện tích",
    },
  ];
  if (property.bedrooms !== null) {
    cells.push({
      icon: <BedDouble aria-hidden="true" className="size-3" />,
      value: property.bedrooms,
      label: "Phòng ngủ",
    });
  }
  if (property.bathrooms !== null) {
    cells.push({
      icon: <Bath aria-hidden="true" className="size-3" />,
      value: property.bathrooms,
      label: "Phòng tắm",
    });
  }
  if (property.floor !== null) {
    cells.push({
      icon: <Building2 aria-hidden="true" className="size-3" />,
      value: property.floor,
      label: "Tầng",
    });
  } else if (property.floorBand !== null) {
    // Redacted floor: show the coarse band ("Thấp"/"Trung"/"Cao") instead.
    cells.push({
      icon: <Building2 aria-hidden="true" className="size-3" />,
      value: property.floorBand,
      label: "Tầng",
    });
  }
  // "Trục" (the unit's stack). Redacted with the exact floor — non-privileged
  // viewers get null, so this tile simply doesn't render for them.
  if (property.axis !== null) {
    cells.push({
      icon: <Columns3 aria-hidden="true" className="size-3" />,
      value: property.axis,
      label: "Trục",
    });
  }
  if (property.furniture !== null) {
    cells.push({
      icon: <Sofa aria-hidden="true" className="size-3" />,
      value: propertyFurnitureLabel(property.furniture),
      label: "Nội thất",
    });
  }
  // Brokerage fee shown as a tile alongside the physical specs (detail only —
  // the list view-model carries no fee, so cards never show it).
  const brokerageFee = formatBrokerageFee(
    property.brokerageFeeType,
    property.brokerageFeeValue,
  );
  if (brokerageFee !== null) {
    cells.push({
      icon: <Coins aria-hidden="true" className="size-3" />,
      value: brokerageFee,
      label: "Phí môi giới",
    });
  }
  // Three columns: pad with em-dash placeholders up to the next multiple of 3
  // so rows stay aligned (the full 6 specs land as a tidy 3 + 3; 4 → 3 + 1).
  while (cells.length % 3 !== 0) {
    cells.push({ icon: null, value: "—", label: "—" });
  }
  const tileSurface =
    surface === "background"
      ? "bg-background border-border"
      : "bg-card border-border";

  return (
    <div className="grid grid-cols-3 gap-1.5">
      {cells.map((cell, i) => (
        <div
          key={i}
          className={cn(
            "flex flex-col items-center gap-1 rounded-[10px] border px-2 py-2",
            tileSurface,
          )}
        >
          {cell.icon ? <span className="text-ink-faint">{cell.icon}</span> : null}
          <div className="font-mono text-base leading-none font-bold tracking-tight tabular-nums">
            {cell.value}
            {cell.unit ? (
              <span className="ml-0.5 text-[11px] text-ink-faint">{cell.unit}</span>
            ) : null}
          </div>
          <span className="text-[11px] text-ink-faint">{cell.label}</span>
        </div>
      ))}
    </div>
  );
}
